import React from 'react';
import styled from 'styled-components/macro';
import { Title, Icon, Flex, Body, Button, Tooltip } from '@cognite/cogs.js';
import { Milestone, Rule, Ruleset } from 'types/dt-graphql-apollo-v2';
import { VerticalDivider } from 'components';
import pluralize from 'pluralize';
import { term } from 'utils/project-ruleset';
import { getRelationalConditionsFromString } from 'utils/nested-conditions';
import {
  getRulesCountFromMilestone,
  getRulesCountFromRuleset,
  getRulesetsCountFromMilestone,
  StepName,
  checkIfItemIsDisabled,
  getDisabledTooltipMessage,
} from '../helpers';

/** This function receives an item which is either a Milestone / Ruleset / Rule
 * and shows some specific information ( information picked based on the *stepName* ) about it in a nicely displayed flex. */
export const getItemInformation = (
  item: Milestone | Ruleset | Rule,
  stepName: StepName
) => {
  const showRulesets = stepName === StepName.COLLECTION;
  const objectName = stepName === StepName.RULE ? 'condition' : 'rule';

  let rulesetsCount = 0;
  let rulesCount = 0;

  switch (stepName) {
    case StepName.COLLECTION:
      rulesetsCount = getRulesetsCountFromMilestone(item as Milestone);
      rulesCount = getRulesCountFromMilestone(item as Milestone);
      break;
    case StepName.RULESET:
      rulesCount = getRulesCountFromRuleset(item as Ruleset);
      break;
    case StepName.RULE:
      rulesCount = getRelationalConditionsFromString(
        (item as Rule).rules
      ).length;
      break;
  }

  return (
    <Flex alignItems="center" direction="row" gap={5}>
      {/* Rulesets count and information */}
      {showRulesets && (
        <>
          <>
            <Title level={6}>{rulesetsCount}</Title>
            <Body level={6}>{pluralize(term.ruleset, rulesetsCount)}</Body>
          </>
          <VerticalDivider />
        </>
      )}

      {/* Rules count and information */}
      <>
        <Title level={6}>{rulesCount}</Title>
        <Body level={6}>{pluralize(objectName, rulesCount)}</Body>
      </>
    </Flex>
  );
};

/** This function receives an item which is either a Milestone / Ruleset / Rule and checks whatever the item has any chidlren tied to it.
 *
 * A Milestone can either have children in *milestone.rulesets* or *milestone.rules*.

 * A Ruleset can only have children in *ruleset.rules*.
 *
 * A Rule does not have any children.
 */
export const checkIfItemIsNested = (
  item: Milestone | Ruleset | Rule,
  stepName: StepName
) => {
  if (!item) return false;

  switch (stepName) {
    case StepName.COLLECTION:
      return (
        getRulesCountFromMilestone(item as Milestone) > 0 ||
        getRulesetsCountFromMilestone(item as Milestone) > 0
      );
    case StepName.RULESET:
      return getRulesCountFromRuleset(item as Ruleset) > 0;
    case StepName.RULE:
      return false;
    default:
      return false;
  }
};

type CopyItemProps = {
  title: string;
  item: Milestone | Ruleset | Rule;
  type: StepName;
  isSelected: boolean;
  hasArrow?: boolean;
  destinationRuleset?: Ruleset | null;
  onItemClick: VoidFunction;
  onArrowClick?: VoidFunction;
};

export const CopyItem = ({
  title,
  item,
  type,
  isSelected,
  hasArrow = false,
  destinationRuleset,
  onItemClick,
  onArrowClick,
}: CopyItemProps) => {
  const isNested = checkIfItemIsNested(item, type);
  const canOpenItem = isNested && hasArrow;
  const isDisabled = checkIfItemIsDisabled(item, type, destinationRuleset);
  const toolTipMessage = getDisabledTooltipMessage(
    item,
    type,
    destinationRuleset
  );

  const handleItemClick = () => {
    if (!isDisabled) {
      onItemClick();
    }
  };

  return (
    <Tooltip content={toolTipMessage} disabled={!isDisabled}>
      <StyledItem
        direction="row"
        justifyContent="space-between"
        onClick={handleItemClick}
        isSelected={isSelected && !isDisabled}
        isNested={isNested}
        isDisabled={isDisabled}
      >
        <Flex alignItems="center" direction="row" gap={8}>
          {isSelected && !isDisabled ? (
            <Icon className="checkmark-icon" type="Checkmark" />
          ) : (
            <div style={{ padding: '0 0.5rem' }} />
          )}
          <Title level={6}>{title}</Title>
        </Flex>
        <Flex alignItems="center" gap={5}>
          {getItemInformation(item, type)}
          {canOpenItem && onArrowClick && (
            <StyledButton
              aria-label="Item can be extended to access children elements."
              icon="ArrowRight"
              onClick={(e) => {
                e.stopPropagation();
                onArrowClick();
              }}
              type="ghost"
            />
          )}
        </Flex>
      </StyledItem>
    </Tooltip>
  );
};

const StyledItem = styled(Flex)<{
  isSelected: boolean;
  isNested: boolean;
  isDisabled: boolean;
}>`
  border: 1px solid var(--cogs-greyscale-grey2);
  border-radius: 4px;

  ${(props) =>
    props.isDisabled
      ? `
        background: rgba(0, 0, 0, 0.03); /* surface interactive/disabled */
        &:hover {
          cursor: not-allowed;
        }`
      : `
        box-shadow: var(--cogs-z-4);
        &:hover {
            cursor: pointer;
            background: var(--cogs-greyscale-grey2);
            transition: all 250ms linear;
        }`};

  ${(props) =>
    props.isSelected &&
    `
      background: rgba(74, 103, 251, 0.08); /* surface interactive/toggled-default */
      border: 1px solid var(--cogs-midblue-5);
      transition: all 250ms linear;
      .checkmark-icon {
        color: var(--cogs-text-accent);
      }
      &:hover {
        border: 1px solid var(--cogs-midblue-5);
        background: rgba(74, 103, 251, 0.12); /* surface interactive/toggled-hover */
      }
  `};

  padding: ${(props) =>
    props.isNested ? `8px 16px 8px 16px;` : `16px 54px 16px 16px;`};
`;

const StyledButton = styled(Button)`
  &:hover {
    cursor: pointer;
    background: var(--cogs-greyscale-grey3);
    transition: all 250ms linear;
  }
`;
